"use client";

import React, { useCallback, useState, useEffect } from "react";
import {
  Navbar as NextNavbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuItem,
  Button,
  Link,
} from "@heroui/react";
import { cn } from "@heroui/react";
import Logo from "../logo";
import { usePathname } from "next/navigation";
import { FiSearch } from "@/components/icons";
import SiteSearch from "@/components/search/site-search";
import { ThemeToggle } from "@/components/theme-switch";

// const isProduction = process.env.NEXT_PUBLIC_ENV === "production";

type NavigationItem = { name: string; href: string; external?: boolean };

const navigation: NavigationItem[] =
  // isProduction
  //   ? [
  //       { name: "What we do", href: "/about-techno-music-world" },
  //       { name: "Contact Us", href: "/about-techno-music-world#contact" },
  //     ]
  //   :
  [
    { name: "Home", href: "/" },
    { name: "Music", href: "/music" },
    { name: "News", href: "/news" },
    { name: "Events", href: "/events" },
    { name: "Articles", href: "/articles" },
    { name: "Venues", href: "/venues" },
    //   { name: "Interviews", href: "/interviews" },
    { name: "What we do", href: "/about-techno-music-world" },
    { name: "Contact Us", href: "/about-techno-music-world#contact" },
  ];

const navigation1 =
  // isProduction
  //   ? []
  //   :
  [
    { name: "Home", href: "/" },
    { name: "Music", href: "/music" },
    { name: "News", href: "/news" },
    { name: "Events", href: "/events", external: false },
  ];

const navigation2: NavigationItem[] =
  // isProduction
  //   ? [
  //       { name: "What we do", href: "/about-techno-music-world" },
  //       { name: "Contact Us", href: "/about-techno-music-world#contact" },
  //     ]
  //   :
  [
    { name: "Articles", href: "/articles" },
    { name: "Venues", href: "/venues" },
    //   { name: "Interviews", href: "/interviews" },
    { name: "What we do", href: "/about-techno-music-world" },
    { name: "Contact Us", href: "/about-techno-music-world#contact" },
  ];

// Main navigation items for mobile (always visible)
const mobileMainNavigation = [
  { name: "Home", href: "/" },
  { name: "Music", href: "/music" },
  { name: "Events", href: "/events" },
  { name: "News", href: "/news" },
  { name: "Articles", href: "/articles" },
];

// Additional navigation items for mobile "More" menu
const mobileMoreNavigation: NavigationItem[] = [
  { name: "Venues", href: "/venues" },
  { name: "What we do", href: "/about-techno-music-world" },
  { name: "Contact Us", href: "/about-techno-music-world#contact" },
];

export default function Navbar({
  animated,
  page,
}: {
  animated: boolean;
  page?: string;
}) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const pathName = usePathname();
  useEffect(() => {
    setIsSearchFocused(false);
    setIsMenuOpen(false);
  }, [pathName]);
  const closeSearch = useCallback(() => {
    setIsSearchFocused(false);
    requestAnimationFrame(() => {
      [...document.querySelectorAll<HTMLButtonElement>("[data-search-trigger]")]
        .find((button) => button.getClientRects().length > 0)
        ?.focus();
    });
  }, []);
  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "k") {
        event.preventDefault();
        setIsMenuOpen(false);
        setIsSearchFocused(true);
      }
      if (event.key === "Escape" && isSearchFocused) closeSearch();
    };
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [isSearchFocused, closeSearch]);
  useEffect(() => {
    if (!isSearchFocused) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isSearchFocused]);

  const checkPath = useCallback(
    (href: string) => {
      // Check if href is '/' and pathname is exactly '/'.
      if (href === "/" && pathName === "/") {
        return true;
      }
      // Don't highlight links with hash fragments (like Contact Us)
      if (href.includes("#")) {
        return false;
      }
      // For other paths, check if the pathname starts with the href but isn't exactly the root path
      if (href !== "/" && pathName.startsWith(href)) {
        return true;
      }
      return false;
    },
    [pathName],
  );

  // Close search dropdown when mobile menu changes state
  useEffect(() => {
    if (isMenuOpen) setIsSearchFocused(false);
  }, [isMenuOpen]);

  return (
    <>
      {/* Global blur overlay when search is focused */}
      {isSearchFocused && !isMenuOpen && (
        <div
          className="fixed inset-0 bg-overlay/40 backdrop-blur-sm z-40 pointer-events-auto"
          style={{ pointerEvents: "auto" }}
          onClick={closeSearch}
        />
      )}

      <NextNavbar
        isBordered
        classNames={{
          base: "border-default-100 relative !border-b-1 !border-solid !border-default-100",
          wrapper: "relative max-w-[88rem] w-full justify-center bg-transparent px-0",
          item: "hidden md:flex",
          toggle: "absolute right-6",
        }}
        height="75px"
        isMenuOpen={isMenuOpen}
        onMenuOpenChange={setIsMenuOpen}
      >
        <div className="flex w-full items-center justify-center lg:mx-32 lg:grid lg:max-w-[70rem] lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:gap-6" data-navigation-menu>
          <NavbarContent
            className="hidden h-11 min-w-0 gap-3 whitespace-nowrap bg-transparent lg:flex xl:gap-5"
            justify="start"
          >
            {navigation1.map((item, index) => (
              <NavbarItem key={item.href}>
                <Link
                  href={item.href}
                  target={item.external ? "_blank" : undefined}
                  className={cn(
                    `text-xs xl:text-sm font-atiga-semibold uppercase font-semibold duration-200 text-theme-light hover:text-ink-secondary ${
                      checkPath(item.href) ? "text-accent" : ""
                    }`,
                  )}
                >
                  {item.name}
                </Link>
              </NavbarItem>
            ))}
          </NavbarContent>

          <NavbarBrand className="flex flex-none items-center justify-center">
            <Link href={"/"}>
              <Logo />
            </Link>
          </NavbarBrand>

          <NavbarContent
            className="hidden h-11 min-w-0 gap-3 whitespace-nowrap bg-transparent lg:flex xl:gap-5"
            justify="end"
          >
            {navigation2.map((item, index) => (
              <NavbarItem key={item.href}>
                <Link
                  href={item.href}
                  target={item.external ? "_blank" : undefined}
                  className={cn(
                    `text-xs xl:text-sm font-atiga-semibold uppercase font-semibold duration-200 text-theme-light hover:text-ink-secondary ${
                      checkPath(item.href) ? "text-accent" : ""
                    }`,
                  )}
                >
                  {item.name}
                </Link>
              </NavbarItem>
            ))}
          </NavbarContent>
        </div>

        <div
          role="group"
          aria-label="Site tools"
          data-navigation-tools
          className="absolute right-3 top-0 flex h-[75px] items-center gap-0 lg:right-4 xl:right-6 2xl:right-8"
        >
          <ThemeToggle />
          <button
            type="button"
            data-search-trigger
            aria-label="Search Techno Music World"
            aria-expanded={isSearchFocused}
            onClick={() => {
              setIsMenuOpen(false);
              setIsSearchFocused(true);
            }}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center text-theme-light hover:text-accent focus-visible:outline focus-visible:outline-accent"
          >
            <FiSearch size={19} />
          </button>
        </div>

        <NavbarMenu
          id="mobile-navigation"
          className="top-[calc(var(--navbar-height)_+_41px)] mx-auto max-h-36 max-w-[80vw] bg-canvas/80 py-6 shadow-medium backdrop-blur-md backdrop-saturate-150 z-50 border-default-200 border-[1px] rounded-none"
          motionProps={{
            initial: { opacity: 0, y: -20 },
            animate: { opacity: 1, y: 0 },
            exit: { opacity: 0, y: -20 },
            transition: {
              ease: "easeInOut",
              duration: 0.2,
            },
          }}
        >
          {mobileMoreNavigation.map((item, index) => (
            <NavbarMenuItem
              key={`${item}-${index}`}
              className="flex justify-center"
            >
              <Link
                onPress={() => setIsMenuOpen(false)} // Close the menu on click
                className="text-center text-default-500 font-atiga-semibold uppercase font-semibold duration-200 hover:text-primary"
                href={item.href}
                target={item.external ? "_blank" : undefined}
                size="md"
              >
                {item.name}
              </Link>
            </NavbarMenuItem>
          ))}
        </NavbarMenu>
      </NextNavbar>

      {/* Mobile Navigation Links (below logo) */}
      <div className="lg:hidden w-full border-b border-default-200 bg-canvas/95">
        <div className="max-w-[70rem] mx-auto px-4 py-3">
          <div className="flex justify-center items-center gap-6 flex-wrap">
            {mobileMainNavigation.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  `text-xs font-atiga-semibold uppercase font-semibold duration-200 text-theme-light hover:text-ink-secondary ${
                    checkPath(item.href) ? "text-accent" : ""
                  }`,
                )}
              >
                {item.name}
              </Link>
            ))}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
              className="text-xs font-atiga-semibold uppercase font-semibold duration-200 text-theme-light hover:text-ink-secondary bg-transparent border-none p-0 cursor-pointer"
            >
              More
            </button>
          </div>
        </div>
      </div>

      <SiteSearch open={isSearchFocused} onClose={closeSearch} />
    </>
  );
}
